/**
 * WBS quantity target APIs (same contracts as portal DPR Targets).
 */
import { notifications } from '@mantine/notifications';
import { showErrorNotification } from '../utils/utils';
import { transactionAxios } from './schedulingApis';

const successNotificationColor = 'teal';

export type WbsTargetFrequency = 'DAY' | 'WEEK' | 'MONTH';

export interface WbsTargetRow {
  unitActivityId: string;
  frequency: string;
  targetPeriod: string;
  quantity: number;
  achievedQuantity: number;
}

export interface WbsTargetUpsertItem {
  unitActivityId: string;
  frequency: WbsTargetFrequency;
  targetPeriod: string;
  quantity: number;
}

function asArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value != null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['data', 'payload', 'result', 'scheduleList']) {
      if (Array.isArray(record[key])) return record[key] as unknown[];
    }
  }
  return [];
}

function parseWbsTargetRow(raw: unknown): WbsTargetRow | null {
  if (raw == null || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const unitActivityId = String(row.unitActivityId ?? row.unit_activity_id ?? '').trim();
  if (!unitActivityId) return null;
  const quantity = Number(row.quantity ?? 0);
  const achievedQuantity = Number(row.achievedQuantity ?? row.achieved_quantity ?? 0);
  return {
    unitActivityId,
    frequency: String(row.frequency ?? '').trim().toUpperCase(),
    targetPeriod: String(row.targetPeriod ?? row.target_period ?? '').trim(),
    quantity: Number.isFinite(quantity) ? quantity : 0,
    achievedQuantity: Number.isFinite(achievedQuantity) ? achievedQuantity : 0,
  };
}

export async function fetchWbsTargets(params: {
  projectId: string;
  unitActivityIds: string[];
  frequency: WbsTargetFrequency[];
  targetPeriod?: string;
}): Promise<WbsTargetRow[]> {
  if (!params.projectId || params.unitActivityIds.length === 0) return [];
  try {
    const payload: Record<string, unknown> = {
      projectId: params.projectId,
      unitActivityIds: params.unitActivityIds,
      frequency: params.frequency,
    };
    if (params.targetPeriod) payload.targetPeriod = params.targetPeriod;

    const res = await transactionAxios.post('dpr/apis/wbs/fetch', payload);
    return asArray(res.data)
      .map(parseWbsTargetRow)
      .filter((row): row is WbsTargetRow => row != null);
  } catch (err) {
    console.error(`Error fetching WBS targets: ${err}`);
    showErrorNotification(err, `Sorry! Failed to fetch look-ahead quantities.`);
    throw err;
  }
}

export async function upsertWbsTargets(params: {
  projectId: string;
  scheduleList: WbsTargetUpsertItem[];
}): Promise<unknown> {
  try {
    const res = await transactionAxios.post(
      'dpr/apis/wbs',
      { projectId: params.projectId, scheduleList: params.scheduleList },
      { headers: { 'Content-Type': 'application/json' } },
    );
    notifications.show({
      title: 'Success',
      message: 'Look-ahead quantities saved successfully!',
      color: successNotificationColor,
    });
    return res.data;
  } catch (err) {
    console.error(`Error upserting WBS targets: ${err}`);
    showErrorNotification(err, `Sorry! Look-ahead quantities could not be saved.`);
    throw err;
  }
}

/**
 * Auto-generate / re-calculate daily WBS targets for a calendar month.
 * Same contract as portal DPR Daily Targets (`targetPeriod` = YYYY-MM).
 */
export async function reCalculateWbsTargets(params: {
  projectId: string;
  /** Calendar month as YYYY-MM */
  targetPeriod: string;
}): Promise<unknown> {
  try {
    const res = await transactionAxios.post('dpr/apis/wbs/autoGenerate', {
      projectId: params.projectId,
      targetPeriod: params.targetPeriod,
    });
    notifications.show({
      title: 'Success',
      message: 'Look-ahead quantities have been re-calculated successfully!',
      color: successNotificationColor,
    });
    return res.data;
  } catch (err) {
    console.error(`Error re-calculating WBS targets: ${err}`);
    showErrorNotification(err, `Sorry! Failed to re-calculate look-ahead quantities.`);
    throw err;
  }
}
